<template>
	<p-toolbar dense class="lp-edit-toolbar transparent elevation-0" :class="{ dark: slideStyleData.dark }">
		<p-toolbar-action icon="mdi-fullscreen" tooltip="Fit Zoom" @click="v => $emit('zoomFit')" />

		<v-divider vertical></v-divider>

		<div>
			<p-toolbar-button
				:disabled="!selectedBlock"
				:tooltip="selectedBlock ? 'Duplicate block' : 'Select a block to duplicate it'"
				icon="mdi-content-duplicate"
				@click="duplicateBlock"
				>Duplicate
			</p-toolbar-button>
			<p-toolbar-button
				:disabled="!selectedBlock"
				:tooltip="selectedBlock ? 'Copy block' : 'Select a block to copy it'"
				icon="mdi-content-copy"
				@click="copyToClipboard(selectedBlock)"
				>Copy
			</p-toolbar-button>
		</div>

		<div v-if="canPaste">
			<p-toolbar-button
				icon="mdi-content-paste"
				:disabled="readClipboardState != 'granted'"
				:tooltip="readClipboardState == 'granted' ? 'Paste block' : 'Clipboard permission not granted'"
				@click="pasteBlock"
				v-if="isInClipboard"
				>Paste
			</p-toolbar-button>
			<p-toolbar-button
				icon="mdi-book-remove"
				tooltip="Remove from clipboard"
				@click="clearClipboard"
				v-if="isInClipboard"
				>Clear Clipboard
			</p-toolbar-button>
		</div>

		<v-divider vertical></v-divider>

		<div>
			<component :is="selectedBlockToolbar" :value="selectedBlock || editingBlock" />
		</div>

		<v-spacer></v-spacer>

		<v-divider vertical></v-divider>
		<v-flex class="sm1">
			<v-select
				v-model="editModeSelect"
				@change="handleChangeSelect"
				:items="editModeSelectItems"
				:hide-details="true"
				solo
				flat
				:dark="lessonPlanController.editLayouts"
				class="lp-edit-toolbar-layout-selector"
			></v-select>
		</v-flex>
		<v-divider vertical></v-divider>

		<p-toolbar-button
			icon="mdi-trash-can-outline"
			@click="onDeleteHandler(selectedBlock.id)"
			color="red"
			:tooltip="
				!selectedBlock || (selectedBlock.protection && !selectedBlock.protection.delete)
					? 'Remove block'
					: 'This block is protected against deletion'
			"
			:disabled="!selectedBlock || (selectedBlock.protection && selectedBlock.protection.delete)"
			>DELETE
		</p-toolbar-button>
	</p-toolbar>
</template>

<script>
import { PToolbarAction, PToolbar, PToolbarButton } from 'plant-common';
import Blocks from '../../../index.js';
import { createNewBlockFrom, convertFileToObject, convertObjectToFile, cloneDeep } from '../../../../utils/utils.js';
import _ from 'lodash';
import { BlockSelectionState } from '../../../_models/blockSelectionState.js';

export default {
	inject: ['blocksSelector', 'lessonPlanController', 'blockRemover', 'slideStyleData'],
	components: {
		PToolbar,
		PToolbarAction,
		PToolbarButton
	},
	data() {
		return {
			isInClipboard: false,
			selectedBlock: null,
			selectedBlockToolbar: null,
			editingBlock: null, // block value when user is "editing-content"
			selectedChildFromEditingBlock: null,
			readClipboardState: undefined,
			editModeSelect: null,
			editModeSelectItems: [
				{ text: 'Slides view', value: 'Slides' },
				{ text: 'Layouts view', value: 'Layouts' }
			]
		};
	},
	created() {
		this.editModeSelect = this.lessonPlanController.editLayouts ? 'Layouts' : 'Slides';
	},
	mounted() {
		this.checkIsInClipboard();

		document.addEventListener('copy', async event => {
			// Copy only when the target is the block
			if (this.selectedBlock && event.target.tagName === 'DIV' && event.target.classList.contains('movable')) {
				this.copyToClipboard(this.selectedBlock);
			}
			this.checkIsInClipboard();
		});
		document.addEventListener('paste', async () => {
			if (this.canPaste) {
				this.pasteBlock();
			}
		});

		document.addEventListener('visibilitychange', event => {
			if (document.visibilityState === 'visible') {
				this.checkIsInClipboard();
			}
		});

		navigator.permissions.query({ name: 'clipboard-read' }).then(permission => {
			this.readClipboardState = permission.state;
			permission.onchange = () => {
				this.readClipboardState = permission.state;
			};
		});

		if (!_.isEmpty(this.blocksSelector)) {
			this.blocksSelector.$on(`selection-changed`, ({ block, state }) => {
				// Do not mark as select block if state is BlockSelectionState.editContent (avoid deleting/duplicate/copy block when editing content)
				if (state === BlockSelectionState.editContent) {
					this.editingBlock = block;
					this.selectedBlock = null;
				} else {
					this.editingBlock = null;
					this.selectedBlock = block;
				}

				if (!block) return;

				const { type } = block;
				if (Blocks[type] && Blocks[type].Toolbar) {
					this.selectedBlockToolbar = Blocks[type].Toolbar;
				} else {
					this.selectedBlockToolbar = { template: '<div></div>' };
				}
			});
			this.blocksSelector.$on(`childrenIdActive`, childId => {
				this.selectedChildFromEditingBlock = childId;
			});
		}
	},
	computed: {
		overlayActive() {
			return this.blocksSelector.overlayActive
		},
		canPaste() {
			return (
				((!this.editingBlock && !this.selectedBlock) ||
					(this.editingBlock && this.editingBlock.type === 'tabs') ||
					(this.editingBlock && this.editingBlock.type === 'carousel') ||
					(this.editingBlock && this.editingBlock.type === 'flashCard') ||
					(this.editingBlock && this.editingBlock.type === 'flipCardEnhanced') ||
					(this.editingBlock && this.editingBlock.type === 'contentMultipleChoiceQuestion') ||
					(this.editingBlock && this.editingBlock.type === 'contentMultipleChoiceQuestionTemplate') ||
					(this.editingBlock && this.editingBlock.type === 'adaptiveLearningResult') ||
					(this.overlayActive && !this.selectedBlock)) &&
				this.isInClipboard
			);
		}
	},
	methods: {
		onDeleteHandler(id) {
			this.blocksSelector.selectParentBlock(id);
			this.blockRemover.deleteBlock(id);
		},
		$addBlockToEditingBlockOrToActiveOverlay(block) {
			switch (this.editingBlock.type) {
				case 'carousel':
					let card = this.editingBlock.data.cards.find(card => card.id == this.selectedChildFromEditingBlock);
					if (card) {
						card.blocks.push(block);
					}
					break;
				case 'tabs':
					let tab = this.editingBlock.data.tabs.find(tab => tab.id == this.selectedChildFromEditingBlock);
					if (tab) {
						tab.blocks.push(block);
					}
					break;
				case 'flipCardEnhanced':
					let flipCardSide = this.editingBlock.data[this.selectedChildFromEditingBlock];
					if (flipCardSide) {
						flipCardSide.blocks.push(block);
					}
					break;
				case 'flashCard':
				case 'contentMultipleChoiceQuestion':
				case 'contentMultipleChoiceQuestionTemplate':
				case 'libraryContentCustom':
					if (this.overlayActive) {
						this.overlayActive.config.blocks.push(block);
					} else {
						this.editingBlock.data.blocks.push(block);
					}
					break;
				case 'adaptiveLearningResult':
					this.editingBlock.data.blocks.push(block);
					break;
				default:
					if (this.overlayActive) {
						this.overlayActive.config.blocks.push(block);
					}
					break;
			}
		},
		duplicateBlock() {
			const newCopiedBlock = createNewBlockFrom(this.selectedBlock, { x: 0, y: 0 });
			let editingBlockId = false;
			const parent = this.blocksSelector.findParentBlock(this.selectedBlock.id);
			// parent: selectedBlock is inside other block
			if (parent) {
				// selectedChildFromEditingBlock: tabs/cards
				// overlayActive: [popup, multipleChoice, flashCard, etc...] overlay selected
				if (this.selectedChildFromEditingBlock || this.overlayActive) {
					this.editingBlock = parent;
					editingBlockId = this.editingBlock.id;
					this.$addBlockToEditingBlockOrToActiveOverlay(newCopiedBlock);
				} else {
					// block is inside other block and in its root (parent.data.blocks)
					parent.data.blocks.push(newCopiedBlock);
				}
			} else {
				this.blocksSelector.blocks.push(newCopiedBlock);
			}
			this.$nextTick(() => {
				this.blocksSelector.selectionClear();
				if (editingBlockId) {
					this.blocksSelector.selectionEdit(editingBlockId);
				}
				this.blocksSelector.selectionAdd(newCopiedBlock.id);
			});
		},
		handleChangeSelect() {
			if (this.editModeSelect === 'Slides') {
				this.lessonPlanController.changeToEditSlides();
			} else {
				this.lessonPlanController.changeToEditLayout();
			}
		},
		async pasteBlock() {
			let block = await this.getFromClipboard();
			if (block) {
				await convertObjectToFile(block);
				const copy = createNewBlockFrom(block, { x: 0, y: 0 });
				var editBlockId = false;
				if (this.editingBlock || this.selectedBlock) {
					if (this.editingBlock) {
						editBlockId = this.editingBlock.id;
						this.blocksSelector.selectionEdit(this.editingBlock.id);
					} else {
						editBlockId = this.selectedBlock.id;
						this.blocksSelector.selectionEdit(this.selectedBlock.id);
					}
					this.$addBlockToEditingBlockOrToActiveOverlay(copy);
				} else {
					this.blocksSelector.blocks.push(copy);
				}
				this.$nextTick(() => {
					this.blocksSelector.selectionClear();
					if (editBlockId) {
						this.blocksSelector.selectionEdit(editBlockId);
					}
					this.blocksSelector.selectionAdd(copy.id);
				});
			}
		},
		clearClipboard() {
			navigator.clipboard.writeText('');
			this.isInClipboard = false;
		},
		async checkIsInClipboard() {
			let block = await this.getFromClipboard();
			this.isInClipboard = block !== null;
		},
		async getFromClipboard() {
			try {
				let text = await navigator.clipboard.readText();
				const json = JSON.parse(text);
				return typeof json === 'object' ? json : null;
			} catch (e) {
				return null;
			}
		},
		copyToClipboard(selectedBlock) {
			// Check if selectedBlock has _file objects
			const clipboardObject = cloneDeep(selectedBlock);
			convertFileToObject(clipboardObject);
			var text = JSON.stringify(clipboardObject);
			navigator.clipboard
				.writeText(text)
				.then(() => {
					this.isInClipboard = true;
				})
				.catch(e => {
					this.isInClipboard = false;
				});
		}
	}
};
</script>

<style>
.lp-edit-toolbar {
	height: 50px !important;
	z-index: 5;
}

.lp-edit-toolbar > .v-toolbar__content {
	padding-left: 0px;
	background-color: #ffffff;
	display: flex;
	flex-wrap: wrap;
	height: auto !important;
}

.lp-edit-toolbar.dark .v-toolbar__content {
	background-color: rgb(66, 66, 66);
	color: #fff;
}

.lp-edit-toolbar.dark .v-btn__content {
	color: #fff;
}

.lp-edit-toolbar.dark .v-btn {
	background-color: transparent !important;
}

.lp-edit-toolbar-layout-selector {
	width: 160px;
	right: 20px;
}
</style>
